import type { InboundArrivalUnitKind } from "./inboundArrivalUnitKind";
import { buildShippingInboundReceivePath } from "../../lib/hubEntityDetailPaths";

export const INBOUND_RECEIVE_ARRIVAL_PACKAGE_ID_SEARCH_KEY = "arrival_package_id";
export const INBOUND_RECEIVE_ARRIVAL_PACKAGE_NO_SEARCH_KEY = "arrival_package_no";
export const INBOUND_RECEIVE_ARRIVAL_ID_SEARCH_KEY = "arrival_id";
export const INBOUND_RECEIVE_ARRIVAL_RMA_NUMBER_SEARCH_KEY = "rma_number";
export const INBOUND_RECEIVE_ARRIVAL_RMA_COUNT_SEARCH_KEY = "rma_count";
export const INBOUND_RECEIVE_ARRIVAL_UNIT_KIND_SEARCH_KEY = "arrival_unit_kind";
export const INBOUND_RECEIVE_RECEIPT_ID_SEARCH_KEY = "receipt_id";

export type InboundReceiveArrivalPackageNav = {
  arrivalPackageId: string;
  arrivalPackageNo: number;
  arrivalId?: string;
  arrivalScan?: string;
  arrivalUnitKind?: InboundArrivalUnitKind;
  rmaNumber?: string | null;
  rmaCountChecked?: boolean;
  rmaReceiveQuantityDiscrepancy?: boolean;
};

export function parseInboundReceiveArrivalPackageSearch(
  searchParams: URLSearchParams,
): Pick<
  InboundReceiveArrivalPackageNav,
  "arrivalPackageId" | "arrivalPackageNo" | "arrivalId" | "arrivalUnitKind" | "rmaNumber" | "rmaCountChecked" | "rmaReceiveQuantityDiscrepancy"
> | null {
  const arrivalPackageId =
    searchParams.get(INBOUND_RECEIVE_ARRIVAL_PACKAGE_ID_SEARCH_KEY)?.trim() ?? "";
  const rawNo = searchParams.get(INBOUND_RECEIVE_ARRIVAL_PACKAGE_NO_SEARCH_KEY);
  const arrivalPackageNo = rawNo != null ? Number.parseInt(rawNo, 10) : Number.NaN;
  if (arrivalPackageId === "" || !Number.isFinite(arrivalPackageNo) || arrivalPackageNo <= 0) {
    return null;
  }
  const arrivalId = searchParams.get(INBOUND_RECEIVE_ARRIVAL_ID_SEARCH_KEY)?.trim() ?? "";
  const rmaNumber = searchParams.get(INBOUND_RECEIVE_ARRIVAL_RMA_NUMBER_SEARCH_KEY)?.trim() ?? "";
  const rmaCountParam = searchParams.get(INBOUND_RECEIVE_ARRIVAL_RMA_COUNT_SEARCH_KEY);
  const rmaCountChecked = rmaCountParam === "ok" || rmaCountParam === "discrepancy";
  const rmaReceiveQuantityDiscrepancy = rmaCountParam === "discrepancy";
  const rawUnitKind = searchParams.get(INBOUND_RECEIVE_ARRIVAL_UNIT_KIND_SEARCH_KEY)?.trim() ?? "";
  const arrivalUnitKind =
    rawUnitKind !== "" ? (rawUnitKind as InboundArrivalUnitKind) : undefined;
  return {
    arrivalPackageId,
    arrivalPackageNo,
    arrivalId: arrivalId === "" ? undefined : arrivalId,
    arrivalUnitKind,
    rmaNumber: rmaNumber === "" ? null : rmaNumber,
    rmaCountChecked,
    rmaReceiveQuantityDiscrepancy,
  };
}

export function mergeInboundReceiveArrivalPackageSearch(
  searchParams: URLSearchParams,
  crate: Pick<
    InboundReceiveArrivalPackageNav,
    | "arrivalPackageId"
    | "arrivalPackageNo"
    | "arrivalId"
    | "arrivalUnitKind"
    | "rmaNumber"
    | "rmaCountChecked"
    | "rmaReceiveQuantityDiscrepancy"
  >,
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  next.set(INBOUND_RECEIVE_ARRIVAL_PACKAGE_ID_SEARCH_KEY, crate.arrivalPackageId);
  next.set(INBOUND_RECEIVE_ARRIVAL_PACKAGE_NO_SEARCH_KEY, String(crate.arrivalPackageNo));
  if (crate.arrivalId != null && crate.arrivalId.trim() !== "") {
    next.set(INBOUND_RECEIVE_ARRIVAL_ID_SEARCH_KEY, crate.arrivalId.trim());
  } else {
    next.delete(INBOUND_RECEIVE_ARRIVAL_ID_SEARCH_KEY);
  }
  if (crate.rmaNumber != null && crate.rmaNumber.trim() !== "") {
    next.set(INBOUND_RECEIVE_ARRIVAL_RMA_NUMBER_SEARCH_KEY, crate.rmaNumber.trim());
  } else {
    next.delete(INBOUND_RECEIVE_ARRIVAL_RMA_NUMBER_SEARCH_KEY);
  }
  if (crate.rmaCountChecked) {
    next.set(
      INBOUND_RECEIVE_ARRIVAL_RMA_COUNT_SEARCH_KEY,
      crate.rmaReceiveQuantityDiscrepancy === true ? "discrepancy" : "ok",
    );
  } else {
    next.delete(INBOUND_RECEIVE_ARRIVAL_RMA_COUNT_SEARCH_KEY);
  }
  if (crate.arrivalUnitKind != null) {
    next.set(INBOUND_RECEIVE_ARRIVAL_UNIT_KIND_SEARCH_KEY, crate.arrivalUnitKind);
  } else {
    next.delete(INBOUND_RECEIVE_ARRIVAL_UNIT_KIND_SEARCH_KEY);
  }
  return next;
}

/** Open the receive workspace on a specific receipt and its dock crate. */
export function buildInboundReceiptReceiveHref(input: {
  sourceType: string;
  sourceId: string;
  receiptId?: string | null;
  arrivalPackageId?: string | null;
  arrivalPackageNo?: string | number | null;
}): string {
  const path = buildShippingInboundReceivePath(input.sourceType, input.sourceId);
  const params = new URLSearchParams();
  const receiptId = input.receiptId?.trim() ?? "";
  if (receiptId !== "" && /^\d+$/.test(receiptId)) {
    params.set(INBOUND_RECEIVE_RECEIPT_ID_SEARCH_KEY, receiptId);
  }
  const packageId = input.arrivalPackageId?.trim() ?? "";
  const packageNo =
    typeof input.arrivalPackageNo === "number"
      ? input.arrivalPackageNo
      : Number.parseInt(input.arrivalPackageNo?.trim() ?? "", 10);
  if (packageId !== "" && /^\d+$/.test(packageId) && Number.isFinite(packageNo) && packageNo > 0) {
    params.set(INBOUND_RECEIVE_ARRIVAL_PACKAGE_ID_SEARCH_KEY, packageId);
    params.set(INBOUND_RECEIVE_ARRIVAL_PACKAGE_NO_SEARCH_KEY, String(packageNo));
  }
  const search = params.toString();
  return search === "" ? path : `${path}?${search}`;
}

export function buildInboundReceiveNavigateTarget(
  sourceType: string,
  sourceId: string,
  crate: InboundReceiveArrivalPackageNav,
  existingSearch?: URLSearchParams | string,
): {
  pathname: string;
  search: string;
  state: InboundReceiveArrivalPackageNav;
} {
  const baseSearch =
    existingSearch instanceof URLSearchParams
      ? existingSearch
      : new URLSearchParams(existingSearch ?? "");
  const search = mergeInboundReceiveArrivalPackageSearch(baseSearch, crate).toString();
  return {
    pathname: buildShippingInboundReceivePath(sourceType, sourceId),
    search,
    state: crate,
  };
}
